import { Avatar, Box, CircularProgress, IconButton, Typography } from '@mui/joy';
import { CaretLeft, UsersThree } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AppProfile } from '../../../components/app/App';
import ServerError from '../../../components/module/ServerError/ServerError';
import SegmentControl from '../../../components/ui/SegmentControl';
import ProfileChatContent from '../../../components/module/ProfileContentShow/ProfileChatContent';
import GroupMembersList from '../../../components/module/Chat/GroupShow/GroupMembersList';
import GroupActions from '../../../components/module/Chat/GroupShow/GroupActions';
import { useChatContentCounts } from '../../../hooks/messages/useChatContent';
import { useGroupMessages } from '../../../hooks/messages/useGroupMessages';
import { apiFetch } from '../../../utils/apiClient';

/**
 * Скругление аватарки группы — не круг: круглая картинка читается как человек,
 * а группа это сущность, а не собеседник. То же значение, что в списке чатов.
 */
const AVATAR_RADIUS = '12px';
const AVATAR_SIZE = 96;

/** Русское склонение: 1 участник, 2 участника, 5 участников. */
function membersLabel(count) {
    const rem10 = count % 10;
    const rem100 = count % 100;

    if (rem10 === 1 && rem100 !== 11) return `${count} участник`;
    if (rem10 >= 2 && rem10 <= 4 && (rem100 < 10 || rem100 > 20)) return `${count} участника`;
    return `${count} участников`;
}

function imageUrl(src) {
    if (!src) return null;
    return src.startsWith('http') ? src : `${process.env.REACT_APP_API_URL}${src}`;
}

/**
 * Страница группы: состав, содержимое переписки и управление.
 *
 * Открывается нажатием на заголовок в шапке группового чата. Название и
 * картинку принимаем через navigate state — их знает шапка чата, причём в самом
 * свежем виде (после переименования). При переходе по прямой ссылке state
 * пустой, и данные берутся из переписки: отдельной ручки «данные группы» на
 * сервере нет, есть только состав участников.
 */
function PageGroup() {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [screen, setScreen] = useState('members');

    // Переписку тянем только ради названия и картинки — и только когда их не
    // передали из шапки чата.
    const needChatInfo = !state?.chatName;
    const { chat, currentUserId } = useGroupMessages(needChatInfo ? chatId : null);

    const [groupName, setGroupName] = useState(state?.chatName ?? '');
    const [groupImage, setGroupImage] = useState(state?.chatImage ?? '');

    useEffect(() => {
        if (!needChatInfo || !chat) return;
        setGroupName(prev => prev || chat.chatName || '');
        setGroupImage(prev => prev || chat.chatImage || '');
    }, [chat, needChatInfo]);

    const loadMembers = useCallback(async () => {
        if (!chatId) return;

        setLoading(true);
        try {
            const res = await apiFetch(`/api/chats/group/${chatId}/members`, { method: 'GET' });
            const data = await res.json();

            if (!res.ok) throw new Error(data?.error ?? 'Не удалось загрузить участников');

            setMembers(Array.isArray(data?.members) ? data.members : []);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [chatId]);

    useEffect(() => { loadMembers(); }, [loadMembers]);

    const { counts } = useChatContentCounts({ groupChatId: chatId });

    const isAdmin = members.some(
        member => Number(member.userId) === Number(currentUserId) && Number(member.userType) === 100
    );

    // Вкладки показываем всегда, в отличие от профиля собеседника: у страницы
    // группы это её оглавление, и исчезающие разделы читались бы как поломка.
    const options = [
        { value: 'members', label: 'Участники' },
        { value: 'media', label: 'Медиа' },
        { value: 'files', label: 'Файлы' },
        { value: 'links', label: 'Ссылки' },
    ];

    const emptyLabel = {
        media: 'В группе пока нет медиа',
        files: 'В группе пока нет файлов',
        links: 'В переписке пока нет ссылок',
    };

    // Пустой раздел не стоит запроса: счётчики уже говорят, что там ничего нет.
    const isEmptyScreen = screen !== 'members' && counts[screen] === 0;

    return (
        <AppProfile title={groupName || 'Группа'} desc="Групповой чат в Аквариум">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <IconButton
                    onClick={() => navigate(`/chat/g/${chatId}`, { state: { direction: -1 } })}
                    sx={{ borderRadius: '50px' }}
                    aria-label="Назад к переписке"
                >
                    <CaretLeft size={20} />
                </IconButton>
                <Typography level="title-md" noWrap>Группа</Typography>
            </Box>

            {error && members.length === 0 ? (
                <ServerError title="Не удалось открыть группу" desc={error} />
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 2, px: 2 }}>
                        {groupImage ? (
                            <Avatar
                                src={imageUrl(groupImage)}
                                alt={groupName}
                                sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, borderRadius: AVATAR_RADIUS }}
                            />
                        ) : (
                            <Box sx={(theme) => ({
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: AVATAR_SIZE,
                                height: AVATAR_SIZE,
                                borderRadius: AVATAR_RADIUS,
                                backgroundColor: theme.palette.primary[500],
                            })}>
                                <UsersThree size={AVATAR_SIZE * 0.42} color="white" weight="fill" />
                            </Box>
                        )}

                        <Typography level="title-lg" sx={{ mt: 1.5, textAlign: 'center', wordBreak: 'break-word' }}>
                            {groupName || 'Групповой чат'}
                        </Typography>

                        {/* Пока состав не загрузился, счётчик не рисуем вовсе:
                            «0 участников» на секунду — это неверные данные. */}
                        {!loading && (
                            <Typography level="body-sm" textColor="text.tertiary">
                                {membersLabel(members.length)}
                            </Typography>
                        )}

                        {isAdmin && (
                            <Typography level="body-xs" textColor="primary.500" sx={{ mt: 0.5, fontWeight: 600 }}>
                                Вы администратор
                            </Typography>
                        )}
                    </Box>

                    <GroupActions
                        chatId={chatId}
                        chatName={groupName}
                        chatImage={groupImage}
                        members={members}
                        isAdmin={isAdmin}
                        onRenamed={setGroupName}
                        onImageUpdated={setGroupImage}
                        onMembersUpdated={setMembers}
                    />

                    <SegmentControl
                        value={screen}
                        options={options}
                        onChange={setScreen}
                        layoutId="group-content-tab"
                        sx={{ mt: 3, mb: 1 }}
                    />

                    {screen === 'members' ? (
                        loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <GroupMembersList
                                members={members}
                                currentUserId={currentUserId}
                                isAdmin={isAdmin}
                                chatId={chatId}
                                onMembersUpdated={setMembers}
                            />
                        )
                    ) : isEmptyScreen ? (
                        <Typography level="body-sm" textColor="text.tertiary" sx={{ py: 4, textAlign: 'center' }}>
                            {emptyLabel[screen]}
                        </Typography>
                    ) : (
                        <ProfileChatContent source={{ groupChatId: chatId }} type={screen} />
                    )}
                </>
            )}
        </AppProfile>
    );
}

export default PageGroup;
